import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, Typography } from '@mui/material';

import { FHTextarea } from '../../../base';
import { MINIMUM_REQUIRED_SENTENCE_LENGTH, Themes } from '../../../../constants';
import { validateSchema, validateSync, getAllInvalidFieldNames } from '../../../../helpers';
import { DischargeSchema, initialDischargeCriteria } from './validationSchema';
import { useTreatmentPlanEditable } from '../hooks/useTreatmentPlanEditable';
import { dischargeTransitionActions } from '../../../../store/treatment-plan/dischargeTransition.slice';

import type { ChangeEvent } from 'react';
import type { IDischargeCriteria } from '../../../../utils/types';

interface DischargeCriteriaProps {
  loading: boolean;
  onUpdateField: (dischargeCriteriaInternal: IDischargeCriteria) => void;
}

function DischargeCriteria({ loading, onUpdateField }: DischargeCriteriaProps) {
  const dispatch = useDispatch();
  const isEditable = useTreatmentPlanEditable() && !loading;
  const listOfKeys = [
    'mastery_of_goals',
    'step_down_in_program_hours',
    'readiness_to_move_on_from_current_service',
    'communication_amongst_providers',
  ];
  const { dischargeCriteria, triggerValidationsOnPageLoad } = useSelector(
    (state: any) => state.dischargeTransitions,
  );
  const [errors, setErrors] = useState<any>({});

  useEffect(() => {
    if (triggerValidationsOnPageLoad && dischargeCriteria) {
      const data = { ...initialDischargeCriteria, ...dischargeCriteria };
      delete data.id;
      const errors = getAllInvalidFieldNames(DischargeSchema, data);
      setErrors(errors);
      return;
    }

    if (!dischargeCriteria || Object.keys(dischargeCriteria).length === 0) {
      return;
    }

    listOfKeys.forEach((key: string) => {
      handleValidate(key, dischargeCriteria);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dischargeCriteria]);

  useEffect(() => {
    let totalErrorCount = 0;
    for (const key in errors) {
      if (errors[key]) {
        totalErrorCount++;
      }
    }
    //Only count entries for when the error exists and is true
    dispatch(
      dischargeTransitionActions.updateState({
        key: 'dischargeErrorCount',
        data: totalErrorCount,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  const handleFieldChange = (key: string, value: string) => {
    dispatch(
      dischargeTransitionActions.updateState({
        key: 'dischargeCriteria',
        data: { ...dischargeCriteria, [key]: value },
      }),
    );
  };

  const handleValidate = (key: string, data: any = dischargeCriteria) => {
    if (!data) return;
    const isValid = validateSchema(DischargeSchema, key, data);
    setErrors((prev: any) => ({ ...prev, [key]: !isValid }));
  };

  const handleFieldBlur = () => {
    const isValid = validateSync(DischargeSchema, dischargeCriteria);
    if (!isValid) return;
    onUpdateField(dischargeCriteria);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <Typography fontWeight="bold" mb={1} textAlign="left">
          Client&apos;s and family&apos;s ability to master the program/goals:*
        </Typography>
        <FHTextarea
          data-testid="masteryOfGoalsTextField"
          fullWidth
          minRows={3}
          maxRows={3}
          sx={{ boxShadow: 'none', borderRadius: '8px' }}
          minLength={MINIMUM_REQUIRED_SENTENCE_LENGTH}
          colorTheme={Themes.Primary}
          disabled={!isEditable}
          expandable={isEditable}
          value={dischargeCriteria?.mastery_of_goals}
          onEdit={(e: ChangeEvent<HTMLTextAreaElement>) =>
            handleFieldChange('mastery_of_goals', e.target.value)
          }
          onBlur={() => handleFieldBlur()}
          onCloseExpandDialog={() => handleFieldBlur()}
          error={errors.mastery_of_goals}
          helperText={
            errors.mastery_of_goals ? `Minimum ${MINIMUM_REQUIRED_SENTENCE_LENGTH} characters` : ''
          }
        />
      </Grid>
      <Grid item xs={6}>
        <Typography fontWeight="bold" mb={1} textAlign="left">
          How and when to step down in program hours:*
        </Typography>
        <FHTextarea
          data-testid="stepDownInProgramHoursTextField"
          fullWidth
          minRows={3}
          maxRows={3}
          minLength={MINIMUM_REQUIRED_SENTENCE_LENGTH}
          sx={{ boxShadow: 'none', borderRadius: '8px' }}
          colorTheme={Themes.Primary}
          disabled={!isEditable}
          expandable={isEditable}
          value={dischargeCriteria?.step_down_in_program_hours}
          onEdit={(e: ChangeEvent<HTMLTextAreaElement>) =>
            handleFieldChange('step_down_in_program_hours', e.target.value)
          }
          onBlur={() => handleFieldBlur()}
          onCloseExpandDialog={() => handleFieldBlur()}
          error={errors.step_down_in_program_hours}
          helperText={
            errors.step_down_in_program_hours
              ? `Minimum ${MINIMUM_REQUIRED_SENTENCE_LENGTH} characters`
              : ''
          }
        />
      </Grid>
      <Grid item xs={6}>
        <Typography fontWeight="bold" mb={1} textAlign="left">
          Client&apos;s readiness to move from the current level of service:*
        </Typography>
        <FHTextarea
          data-testid="readinessToMoveOnFromCurrentServiceTextField"
          fullWidth
          minRows={3}
          maxRows={3}
          minLength={MINIMUM_REQUIRED_SENTENCE_LENGTH}
          sx={{ boxShadow: 'none', borderRadius: '8px' }}
          colorTheme={Themes.Primary}
          disabled={!isEditable}
          expandable={isEditable}
          value={dischargeCriteria?.readiness_to_move_on_from_current_service}
          onEdit={(e: ChangeEvent<HTMLTextAreaElement>) =>
            handleFieldChange('readiness_to_move_on_from_current_service', e.target.value)
          }
          onBlur={() => handleFieldBlur()}
          onCloseExpandDialog={() => handleFieldBlur()}
          error={errors.readiness_to_move_on_from_current_service}
          helperText={
            errors.readiness_to_move_on_from_current_service
              ? `Minimum ${MINIMUM_REQUIRED_SENTENCE_LENGTH} characters`
              : ''
          }
        />
      </Grid>
      <Grid item xs={6}>
        <Typography fontWeight="bold" mb={1} textAlign="left">
          Communication between clinicians and other professionals:*
        </Typography>
        <FHTextarea
          data-testid="communicationAmongstProvidersTextField"
          fullWidth
          minRows={3}
          maxRows={3}
          minLength={MINIMUM_REQUIRED_SENTENCE_LENGTH}
          sx={{ boxShadow: 'none', borderRadius: '8px' }}
          colorTheme={Themes.Primary}
          disabled={!isEditable}
          expandable={isEditable}
          value={dischargeCriteria?.communication_amongst_providers}
          onEdit={(e: ChangeEvent<HTMLTextAreaElement>) =>
            handleFieldChange('communication_amongst_providers', e.target.value)
          }
          onBlur={handleFieldBlur}
          onCloseExpandDialog={() => handleFieldBlur()}
          error={errors.communication_amongst_providers}
          helperText={
            errors.communication_amongst_providers
              ? `Minimum ${MINIMUM_REQUIRED_SENTENCE_LENGTH} characters`
              : ''
          }
        />
      </Grid>
    </Grid>
  );
}

export default DischargeCriteria;
